import type { HeldMessage, HeldNotSentVariant, HeldOutcome, HeldRun } from 'app/types/held-message'

/**
 * Held messages ("send later") are ordinary indexed Message rows with a future
 * `sendDate` and `status: pending`. The `messages` resolver filters only on
 * `archived`, so it returns them, and the API owner has confirmed that is
 * deliberate (TRK-5656): a consumer that does not want queued texts excludes
 * them itself.
 *
 * Every read path that shows the user what has actually been said goes through
 * here, so there is one place to change if that default ever flips. The one
 * consumer that WANTS held messages is HELD_MESSAGES_QUERY, which asks for them
 * by name and must not use this.
 */

/**
 * The status the API writes when it cancels a held message, and the only thing
 * left on the row that identifies it as one.
 *
 * Verified against trackable-api on 2026-09-30. Two l's is written in exactly
 * three places and every one of them is a held-message path:
 * `cancelScheduledMessage` in api/graphql/resolvers/message.ts, and the
 * reply-before-send and opt-out branches of `dispatchDueScheduledMessages` in
 * bin/workflow-run-resume.ts. The provider vocabulary documented on
 * `Message.status` spells it `canceled` with one l, and the only writer of a
 * provider status is the Twilio delivery callback in api/rest/twilio.ts, which
 * assigns the webhook's value verbatim. So the two spellings never collide.
 *
 * Keying on the status is not a stylistic choice. All three cancel paths also
 * set `sendDate` to null, and a user-initiated cancel leaves `errorCode` null
 * too, so a cancelled held row has no other field that separates it from an
 * ordinary sent message. Anything that tests `sendDate` presence to recognize a
 * held row lets a cancelled one through, and it then reads as sent.
 */
export const HELD_CANCELLED_STATUS = `cancelled`

/** The subset of a Message row this module classifies. */
type StatusRow = { status?: string | null; errorCode?: string | null; sendDate?: string | null }

/**
 * Whether a row belongs to the scheduling feature rather than to the record of
 * what was actually said.
 *
 * `pending` and `cancelled` are held-only. `failed` is NOT, which is why it is
 * never matched on the status alone: the Twilio callback writes `failed` for a
 * carrier failure on a text that really was dispatched, and
 * `Message.createFailed` writes it for an immediate send refused over consent.
 * Both are real history the user has to keep seeing, and a failed outbound
 * bubble already renders with a not-delivered marker, so it does not read as
 * sent in the timeline either way.
 *
 * The one held path that writes `failed` pairs it with `NO_PHONE`, and no
 * ordinary path can produce that pair: `NO_PHONE` is thrown by
 * `Message.sendForContact` before any row exists, so queueing for a contact
 * with no cell number is refused outright and nothing is persisted. (That also
 * makes the poller's own NO_PHONE branch unreachable today, because the
 * instance `send()` it calls never throws NO_PHONE. The pair is matched here
 * regardless, so the spec's third not-sent state works the moment the API can
 * reach it, and matching it costs nothing while nothing else writes it.)
 */
export function isHeldRow(row: StatusRow): boolean {
  // `pending` alone is not enough, and only here. A scheduled message is created
  // with its future `sendDate`, so a pending row without one was never scheduled:
  // it is a row the API left behind on a send that failed another way, and dev
  // holds them going back months. Treating those as queued invents a tray saying
  // a message will send that never will.
  //
  // The cancelled branch keeps testing the status alone, because all three cancel
  // paths clear `sendDate` and a sendDate test there would miss every cancelled
  // held message.
  if (row.status === `pending`) return !!row.sendDate
  if (row.status === HELD_CANCELLED_STATUS) return true
  return row.status === `failed` && row.errorCode === `NO_PHONE`
}

/**
 * Whether the tray and the Scheduled list should surface a held row, as opposed
 * to merely keeping it out of the timeline.
 *
 * A row the user cancelled themselves is dropped. `cancelScheduledMessage`
 * leaves `errorCode` null, so a user cancel is exactly a cancelled row with no
 * code. The spec's not-sent table is three rows long and every one of them is
 * the world changing under a queued message: they replied, they opted out, no
 * number on file. It also reserves amber for those states. A cancel the user
 * just performed is none of that, and an amber band saying their message was
 * not sent reads as a failure report for something they chose. The generic
 * "This message was not sent." stays for an `errorCode` this UI does not
 * recognize, which is a real thing to report.
 *
 * Hiding such a row from the tray must never put it back in the timeline: it
 * still never went out. Callers building a timeline exclusion use `isHeldRow`.
 */
export function isHeldRowToShow(row: StatusRow): boolean {
  if (!isHeldRow(row)) return false
  return !(row.status === HELD_CANCELLED_STATUS && !row.errorCode)
}

/**
 * The held-message exclusion for a search, keyed on the status vocabulary.
 *
 * Both clauses are load-bearing. `pending` is a message still waiting to go out.
 * `cancelled` is one that never will, and because the cancel clears `sendDate`
 * it is otherwise indistinguishable from an ordinary sent message: without this
 * clause it becomes a conversation's preview text and bumps the thread to the
 * top of a newest-sorted list, which is the exact failure this feature exists
 * to prevent.
 *
 * `failed` is deliberately absent. See `isHeldRow`: it is shared with real
 * carrier failures and consent refusals, which are history the user needs. The
 * one held `failed` shape is identified by `errorCode`, and putting an errorCode
 * clause into the search every message list in the app runs through is not a
 * trade worth making for a state the API cannot currently reach.
 *
 * KNOWN EXCEPTION to the "one place" claim above: `useSidebarCounts.tsx` spells
 * a `NOT status:pending` clause out inside its `UNREAD_MESSAGES_QUERY` search
 * literal, because a `graphql()` document is plucked at build time and cannot
 * call a function. That query does not need this constant's cancelled clause;
 * its own note explains why.
 */
export const EXCLUDE_HELD = `NOT status:pending AND NOT status:${HELD_CANCELLED_STATUS}`

/**
 * A search that matches every message except a held one, used when there is no
 * other search to AND the exclusion onto.
 *
 * A bare leading `NOT` matches nothing in this search backend, so the exclusion
 * cannot stand alone: it needs a positive clause to subtract from. `locationId`
 * is the field to use because it is non-nullable on `Message` and already
 * searched everywhere, so `locationId:*` is every message and nothing else. A
 * nullable field such as `status` would silently drop rows that have none.
 */
export const EXCLUDE_HELD_ALONE = `locationId:* AND ${EXCLUDE_HELD}`

/**
 * Adds the held-message exclusion to a search, ANDed at the top level.
 *
 * Top level matters: a bare leading NOT inside a parenthesised group matches
 * nothing in this search backend, so an existing `field:(a OR b)` group is left
 * untouched and the exclusion is appended outside it. For the same reason, an
 * empty search gets a positive clause in front of the NOT rather than the NOT
 * on its own, which would match nothing and empty every list it filtered.
 */
export function excludeHeld(search?: string): string {
  const trimmed = search?.trim()
  if (!trimmed) return EXCLUDE_HELD_ALONE
  if (trimmed.includes(EXCLUDE_HELD)) return trimmed
  return `${trimmed} AND ${EXCLUDE_HELD}`
}

/** A row as the typed `messages` document returns it: nullable fields as `T | null`. */
type HeldMessageRow = {
  [K in keyof HeldMessage]: HeldMessage[K] | null
}

/**
 * Held rows ready for the UI, with the null/undefined mismatch normalized once.
 *
 * A `sent` or `delivered` row is dropped because it is an ordinary message now
 * and the thread renders it. A not-sent row is KEPT when it still has something
 * to say: it is how the user learns their message did not go out and why. See
 * `isHeldRowToShow` for what that leaves out and why.
 */
export function toHeldMessages(rows: readonly HeldMessageRow[] | null | undefined): HeldMessage[] {
  return (rows ?? [])
    .filter((row) => isHeldRowToShow(row))
    .map((row) => ({
      id: row.id!,
      text: row.text!,
      sendDate: row.sendDate ?? undefined,
      status: row.status ?? undefined,
      errorCode: row.errorCode ?? undefined,
      contactId: row.contactId ?? undefined,
      threadId: row.threadId ?? undefined,
      to: row.to!,
      from: row.from!,
      sentByUserId: row.sentByUserId ?? undefined,
      createdDate: row.createdDate!,
      read: row.read ?? undefined,
    }))
}

/** Why a held message did not send, in the user's words, keyed off `errorCode`. */
const NOT_SENT_REASONS: Record<string, { variant: HeldNotSentVariant; reason: string; canSendAnyway: boolean }> = {
  CONTACT_REPLIED: { variant: `replied`, reason: `They replied before this went out.`, canSendAnyway: true },
  OPTED_OUT: { variant: `opted_out`, reason: `They opted out of SMS.`, canSendAnyway: false },
  NO_PHONE: { variant: `no_phone`, reason: `No cell number on file.`, canSendAnyway: false },
  // Written by the poller's agent gate (TRK-5939): the AI agent held the thread at send
  // time, so the scheduled message was cancelled rather than fired alongside the agent's
  // replies. `canSendAnyway` is false because offering a manual send here re-creates the
  // exact collision the gate exists to prevent, and the composer is withheld on an agent
  // thread for the same reason.
  AI_AGENT_ACTIVE: {
    variant: `agent_active`,
    reason: `The AI Agent is replying on this thread.`,
    canSendAnyway: false,
  },
}

/**
 * What the tray should say about one held message.
 *
 * Data only. The `variant` names which situation this is so a caller can give it
 * its own icon and tone; it carries no icon, colour or class, because a lib does
 * not decide presentation.
 *
 * `canSendAnyway` is false for an opt-out on purpose: a control that offers to
 * text someone who replied STOP must not exist, even though the API would refuse
 * the send. It is also false for an unrecognized code, because offering to send
 * on a reason we cannot explain is worse than offering nothing. It remains the
 * only answer to "may a send be offered"; `variant` must never be consulted for
 * that, or the two answers drift.
 */
export function heldOutcome(message: HeldMessage): HeldOutcome {
  if (message.status === `pending`) return { kind: `queued` }
  const known = message.errorCode ? NOT_SENT_REASONS[message.errorCode] : undefined
  return {
    kind: `not_sent`,
    variant: known?.variant ?? `unknown`,
    reason: known?.reason ?? `This message was not sent.`,
    canSendAnyway: known?.canSendAnyway ?? false,
  }
}

/**
 * Queued messages grouped into the runs that produced them.
 *
 * A bulk send creates one held message per contact, so the Scheduled list would
 * otherwise show four hundred rows for one action. Grouping is client-side by
 * body, send time, and author: there is no campaign entity, and the only thing
 * this grouping needs to support is cancelling a run in one go.
 *
 * Only `pending` rows group. A cancelled or failed one carries its own reason
 * and is shown on its own, because it needs a decision that its siblings do not.
 */
export function groupHeldRuns(messages: readonly HeldMessage[]): HeldRun[] {
  const runs = new Map<string, HeldRun>()
  for (const message of messages) {
    if (message.status !== `pending` || !message.sendDate) continue
    const key = `${message.sentByUserId ?? ``}|${message.sendDate}|${message.text}`
    const run = runs.get(key)
    if (run) run.ids.push(message.id)
    else runs.set(key, { key, text: message.text, sendDate: message.sendDate, ids: [message.id] })
  }
  return [...runs.values()]
}
